import api from '@shared/api';
import { isEmpty, flatMap } from 'lodash/fp';
import { BuildingSearchResult, BuildingMapMarker } from '@root/types';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { addListener, MAP_MAX_ZOOM } from '@shared/googleMaps';
import ListingSearchCriteria from '../../components/layouts/Truva/ListingSearchDeprecated/utils/Criteria/ListingSearchCriteria';
import MarkerGenerator from './MarkerGenerator/MarkerGenerator';
import BuildingInfoWindow from './BuildingInfoWindow';

type MarkerTrackers = {
  trackBuildingViewedOnMap: () => null;
  trackMapClickToListing: () => null;
  trackMapDeclustered: () => null;
};

class Markers {
  /* eslint-disable @typescript-eslint/lines-between-class-members */
  isClustered: boolean;
  isEventHandlersEnabled: boolean;
  markers: google.maps.Marker[];
  markerGenerator: MarkerGenerator;
  markerClusterer: MarkerClusterer | null;
  trackers: MarkerTrackers;
  activeMarker: google.maps.Marker | null;
  activeBuilding: BuildingSearchResult | null;
  buildingInfoWindow: BuildingInfoWindow;
  /* eslint-enable @typescript-eslint/lines-between-class-members */

  constructor(isClustered, trackers, buildingInfoWindow, isEventHandlersEnabled) {
    this.trackers = trackers;
    this.isClustered = isClustered || false;
    this.markerGenerator = new MarkerGenerator();
    this.buildingInfoWindow = buildingInfoWindow;
    this.markerClusterer = null;
    this.activeBuilding = null;
    this.activeMarker = null;
    this.markers = [];
    this.isEventHandlersEnabled = isEventHandlersEnabled;
  }

  createMarkers = (buildingSearchResults: BuildingSearchResult[], map, selectedMarkets) => {
    this.clearAllMarkers();
    const marketBounds = flatMap('envelope', selectedMarkets);

    if (buildingSearchResults.length === 0 && isEmpty(marketBounds)) return;

    this.markers = buildingSearchResults.map(building => this.createMarker(building, map));

    if (this.isClustered && this.markers.length) {
      this.markerClusterer = new MarkerClusterer(map, this.markers, {
        styles: [
          this.markerGenerator.getSmallClusterIcon(),
          this.markerGenerator.getSmallClusterIcon(true),
          this.markerGenerator.getLargeClusterIcon(),
          this.markerGenerator.getLargeClusterIcon(true),
        ], // this should be a constant somewhere
        calculator: this.markerGenerator.calculateClusterStyle,
        zoomOnClick: true,
        maxZoom: MAP_MAX_ZOOM - 1,
      } as any);

      addListener(this.markerClusterer, 'clusterclick', cluster => {
        // This resolves an issue where sometimes the cluster you
        // clicked on doesnt zoom because its already at a good
        // zoom level.
        const zoom = map.getZoom();
        if (zoom + 1 <= MAP_MAX_ZOOM) {
          map.setCenter(cluster.getCenter());
          map.setZoom(zoom + 1);
        }
        this.trackers.trackMapDeclustered();
      });
    }
  };

  createMarker = (building: BuildingSearchResult, map: google.maps.Map) => {
    const marker = this.markerGenerator.createMarker(
      map,
      building,
      this.isClustered,
      this.isEventHandlersEnabled,
    );
    const listingCount = building.count;

    if (this.isEventHandlersEnabled) {
      marker.addListener('click', () => {
        this.handleMarkerOnClick(building, marker, map);
      });

      marker.addListener('mouseover', () => {
        this.handleMarkerMouseOver(building, marker);
      });

      marker.addListener('mouseout', () => {
        if (!this.activeMarker || this.activeMarker !== marker) {
          marker.setIcon(
            this.markerGenerator.getMarkerIcon(listingCount, { exclusive: building.exclusive }),
          );
        }
      });
    }

    return marker;
  };

  clearAllMarkers = () => {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
  };

  handleMarkerMouseOver = (building: BuildingSearchResult, marker: google.maps.Marker) => {
    marker.setIcon(
      this.markerGenerator.getMarkerIcon(building.count, {
        highlighted: true,
        exclusive: building.exclusive,
      }),
    );
  };

  handleMarkerOnClick = async (
    building: BuildingSearchResult,
    marker: google.maps.Marker,
    map: google.maps.Map,
  ) => {
    this.removeActiveMarker();

    this.activeMarker = marker;
    this.activeBuilding = building;

    const markerData: BuildingMapMarker = await this.fetchBuildingMapMarkers(building.slug);
    const listingCount = building.count;

    if (this.buildingInfoWindow) {
      this.buildingInfoWindow.setInfoWindow(marker, map, markerData.listings);
    }

    this.trackers.trackBuildingViewedOnMap();

    marker.setIcon(
      this.markerGenerator.getMarkerIcon(listingCount, {
        highlighted: true,
        exclusive: building.exclusive,
      }),
    );
  };

  removeActiveMarker = () => {
    if (this.activeMarker && this.activeBuilding) {
      this.activeMarker.setIcon(
        this.markerGenerator.getMarkerIcon(this.activeBuilding.count, {
          highlighted: false,
          exclusive: this.activeBuilding.exclusive,
        }),
      );
      this.activeMarker = null;
      this.activeBuilding = null;
    }
  };

  getMarkers = () => this.markers;

  fetchBuildingMapMarkers = async (buildingSlug: string) => {
    const criteria = ListingSearchCriteria.fromUrl({
      queryString: window.location.search,
    });
    const response = await api.fetch(criteria.toApiMapMarkerUrl(buildingSlug));
    return response.json();
  };
}

export default Markers;
